import {
  type DermloopClinicalActionTypeEnum,
  DermloopUserRoleEnum,
  SUPPORTED_CLINICAL_ACTION_TYPES,
} from '@dermloop/static-values';

export interface ClinicalFormPermissionInput {
  /** The caller's accepted, unexpired affiliation at the case's unit. */
  affiliation: {
    roles?: readonly DermloopUserRoleEnum[] | null;
    readonly?: boolean | null;
    allowedClinicalActions?: readonly DermloopClinicalActionTypeEnum[] | null;
  } | null;
  /** Every role the caller holds at the case's unit, whatever its source. */
  unitRoles?: readonly DermloopUserRoleEnum[] | null;
  conditionTypeGranted: boolean;
  encounterRequired: boolean;
  hasEncounter: boolean;
  /** The case has completed information; false only while registering a new case. */
  submitted: boolean;
}

export interface ClinicalFormPermissions {
  accessDeniedReason?: 'no-clinical-role' | 'condition-type-not-granted';
  canSaveDraft: boolean;
  canCompleteClinical: boolean;
  completionBlockedReason?:
    | 'read-only'
    | 'ehr-managed-submitted'
    | 'assistant-draft-only'
    | 'encounter-required';
  /** Empty when completion is blocked; never falls back to unit defaults. */
  allowedNewActionTypes: DermloopClinicalActionTypeEnum[];
}

/**
 * Authorization for the configured clinical form, shared by the server and both
 * hosts. Completing clinical information never needs an action permission, but
 * an action never bypasses the completion restrictions.
 */
export function getClinicalFormPermissions(
  input: ClinicalFormPermissionInput,
): ClinicalFormPermissions {
  const roles = [
    ...(input.affiliation?.roles ?? []),
    ...(input.unitRoles ?? []),
  ];
  if (
    !input.affiliation?.roles?.some(
      (role) =>
        role === DermloopUserRoleEnum.MedicalUnitParticipant ||
        role === DermloopUserRoleEnum.MedicalUnitAssistant,
    )
  )
    return denied('no-clinical-role');
  if (!input.conditionTypeGranted) return denied('condition-type-not-granted');
  // As in legacy, an EHR-managed group's submitted case is read-only for everyone.
  const lockedReason = input.affiliation.readonly
    ? 'read-only'
    : input.encounterRequired && input.submitted
      ? 'ehr-managed-submitted'
      : undefined;
  const canSaveDraft = !lockedReason;
  const completionBlockedReason =
    lockedReason ??
    (roles.includes(DermloopUserRoleEnum.MedicalUnitAssistant)
      ? 'assistant-draft-only'
      : input.encounterRequired && !input.hasEncounter
        ? 'encounter-required'
        : undefined);
  if (completionBlockedReason)
    return {
      canSaveDraft,
      canCompleteClinical: false,
      completionBlockedReason,
      allowedNewActionTypes: [],
    };
  return {
    canSaveDraft,
    canCompleteClinical: true,
    allowedNewActionTypes: SUPPORTED_CLINICAL_ACTION_TYPES.filter((type) =>
      input.affiliation?.allowedClinicalActions?.includes(type),
    ),
  };
}

/** Images are edited in Capture; desktop shows them read-only. */
export function canEditClinicalImages(host: 'capture' | 'desktop'): boolean {
  return host === 'capture';
}

function denied(
  accessDeniedReason: ClinicalFormPermissions['accessDeniedReason'],
): ClinicalFormPermissions {
  return {
    accessDeniedReason,
    canSaveDraft: false,
    canCompleteClinical: false,
    allowedNewActionTypes: [],
  };
}
